import { track } from '@amplitude/analytics-browser'
import {
  ActForm,
  ApprovalState,
  InspectionState,
  SignatureState,
  User,
  UUID,
  WorkDocSection,
  WorkType,
} from '@builddocs/data-management'
import { IFaceControlCtx, SignHandlerParams } from '@builddocs/feature-faceid'
import {
  AOSRType,
  getBlobAOSR,
  getDigestAndPrepareAOSR,
  getDigestAndPrepareSecondaryAct,
  inspectAct,
  inspectInitAct,
  inspectInitSecondaryAct,
  inspectSecondaryAct,
  SecondaryActType,
  signAct,
  signInitAct,
  signInitSecondaryAct,
  signSecondaryAct,
} from '@builddocs/filestore-api'
import { ToastType } from '@builddocs/system'
import { AxiosError } from 'axios'
import _ from 'lodash'
import { toast } from 'react-toastify'

type InspectActType = {
  user: User
  act: AOSRType | SecondaryActType
  projectId: UUID
  contractId: UUID
  selectedWorkDocSection?: Pick<WorkDocSection, 'workDocSection'>
  selectedInspectors: UUID[]
  refreshAct: () => void
  setIsLoading: (v: boolean) => void
  toast: ToastType
}

type SigningActType = {
  isEmbedded: boolean
  user: User
  act: AOSRType | SecondaryActType
  projectId: UUID
  contractId: UUID
  selectedWorkDocSection?: Pick<WorkDocSection, 'workDocSection'>
  refreshAct: () => void
  selectedSigners: UUID[]
  setIsLoading: (v: boolean) => void
  setIsNeedUpdate: (v: boolean) => void
  faceControl: IFaceControlCtx
}

export const handleToInspect = async (args: InspectActType) => {
  const {
    user,
    selectedInspectors,
    setIsLoading,
    act,
    projectId,
    contractId,
    selectedWorkDocSection,
    refreshAct,
    toast,
  } = args

  if (!user) return { skipped: true }
  if (!selectedInspectors.length) {
    toast.warning(
      'Нельзя отправить на проверку! Отметьте проверяющих в договоре и укажите соответствующий вид работ',
    )
    return { skipped: true }
  }
  setIsLoading(true)
  if (act.actType === ActForm.AOSR) {
    try {
      if (
        act.approvalStatus === ApprovalState.NEW ||
        act.approvalStatus === ApprovalState.RECREATED
      ) {
        if (window.confirm('Отправить на проверку?')) {
          return inspectInitAct(
            projectId,
            contractId,
            selectedWorkDocSection?.workDocSection ?? '',
            act.id,
            selectedInspectors,
          )
            .then(data => {
              refreshAct()
              toast.success('Отправлено на согласование')
              return data
            })
            .catch(error => {
              toast.error(error?.response?.data?.message)
              return { skipped: true }
            })
            .finally(() => {
              setIsLoading(false)
            })
        } else {
          setIsLoading(false)
          return { skipped: true }
        }
      } else {
        await inspectAct(
          act.id,
          user.employment.employmentId,
          InspectionState.ACCEPTED,
        )
        refreshAct()

        const inspectorsWhoPending = act.inspectors.filter(
          inspector => inspector.inspectionState === InspectionState.PENDING,
        )
        const inspectorsWhoAccepted = act.inspectors.filter(
          inspector => inspector.inspectionState === InspectionState.ACCEPTED,
        )

        if (
          inspectorsWhoAccepted.length === act.inspectors.length ||
          (inspectorsWhoPending.length === 1 &&
            act.inspectors.length - 1 === inspectorsWhoAccepted.length &&
            inspectorsWhoPending[0].employmentId ===
              user.employment.employmentId)
        ) {
          await signInitAct({
            isEmbedded: true,
            actId: act.id,
            projectId,
            journalId: contractId,
          })
          refreshAct()
          toast.success('Отправлено на подпись')
        }

        toast.success('Согласовано')
      }
    } catch (error) {
      // @ts-expect-error затычка
      toast.error(error?.response?.data?.message)
      setIsLoading(false)
    }
  } else {
    try {
      if (
        act.approvalStatus === ApprovalState.NEW ||
        act.approvalStatus === ApprovalState.RECREATED
      ) {
        if (window.confirm('Отправить на проверку?')) {
          return inspectInitSecondaryAct(
            projectId,
            contractId,
            selectedWorkDocSection?.workDocSection ?? '',
            act.id,
            selectedInspectors,
          )
            .then(data => {
              refreshAct()
              toast.success('Отправлено на согласование')
              return data
            })
            .catch(error => {
              toast.error(error?.response?.data?.message)
              return { skipped: true }
            })
            .finally(() => setIsLoading(false))
        } else {
          setIsLoading(false)
          return { skipped: true }
        }
      } else {
        await inspectSecondaryAct(
          act.id,
          user.employment.employmentId,
          InspectionState.ACCEPTED,
        )
        refreshAct()

        const inspectorsWhoPending = act.inspectors.filter(
          inspector => inspector.inspectionState === InspectionState.PENDING,
        )
        const inspectorsWhoAccepted = act.inspectors.filter(
          inspector => inspector.inspectionState === InspectionState.ACCEPTED,
        )

        if (
          inspectorsWhoAccepted.length === act.inspectors.length ||
          (inspectorsWhoPending.length === 1 &&
            act.inspectors.length - 1 === inspectorsWhoAccepted.length &&
            inspectorsWhoPending[0].employmentId ===
              user.employment.employmentId)
        ) {
          await signInitSecondaryAct({
            isEmbedded: true,
            actId: act.id,
            journalId: contractId,
          })
          refreshAct()
          toast.success('Отправлено на подпись')
        }
        toast.success('Согласовано')
      }
    } catch (error) {
      const e = error as AxiosError

      toast.error(e?.response?.data?.message ?? 'Неизвестная ошибка')
      setIsLoading(false)
    }
  }
}

/**
 * Отправка акта на подписание или само подписание
 * подписью (true) или откреплённой (false)
 * @param args
 */
export const handleToSigning = async (args: SigningActType) => {
  const {
    isEmbedded,
    user,
    selectedSigners,
    setIsLoading,
    act,
    projectId,
    contractId,
    refreshAct,
    setIsNeedUpdate,
    faceControl,
  } = args
  const isAosr = act.actType === ActForm.AOSR

  if (act.approvalStatus === ApprovalState.APPROVED) {
    if (!user) return
    if (!selectedSigners.length) {
      toast.warning(
        'Нельзя отправить на подпись! Отметьте уполномоченных в договоре и укажите соответствующий вид работ.',
      )
      return
    }
    setIsLoading(true)
    if (isAosr) {
      signInitAct({
        projectId,
        journalId: contractId,
        actId: act.id,
        isEmbedded,
      })
        .then(() => {
          refreshAct()
          toast.success('Акт отправлен на подпись')
          setIsLoading(false)
        })
        .catch(err => {
          toast.warning('Нельзя отправить на подпись!')
          toast.warning(err)
          if (err.response.data.statusCode === 120224) {
            setIsNeedUpdate(true)
          }
        })
    } else {
      signInitSecondaryAct({
        journalId: contractId,
        actId: act.id,
        isEmbedded,
      })
        .then(() => {
          refreshAct()
          toast.success('Акт отправлен на подпись')
          setIsLoading(false)
        })
        .catch(err => {
          toast.warning('Нельзя отправить на подпись!')
          toast.warning(err)
          if (err.response.data.statusCode === 120224) {
            setIsNeedUpdate(true)
          }
        })
    }
  } else {
    // Открываем диалог верификации и передаём коллбэк для подписи
    faceControl.openFaceControl({
      isEmbedded: act.signatureEmbedded,
      getDigestHandler: async ({
        signedHash,
        keyIssuer,
        imprint,
        facePhoto,
        signerName,
        keySerialNumber,
        certificateDateStart,
        certificateDateEnd,
        withPoa,
        poaId,
        poaDateStart,
        poaDateEnd,
      }) => {
        if (isAosr) {
          let digest: string
          let actRegistryDigest: string | undefined

          if (act.signatureEmbedded) {
            const aosrDigestResponse = await getDigestAndPrepareAOSR({
              base64signature: signedHash,
              documentId: act.id,
              employmentId: user.employment.employmentId,
              imprint: imprint,
              keyIssuer: keyIssuer,
              signatureState: SignatureState.PENDING,
              certificateDateStart,
              certificateDateEnd,
              name: signerName,
              faceId: {
                faceImage: facePhoto,
                keySerialNumber,
              },
              withPoa,
              poaId,
              poaDateStart,
              poaDateEnd,
            })

            digest = aosrDigestResponse.digest
            actRegistryDigest = aosrDigestResponse.actRegistryDigest
          } else {
            digest = await getBlobAOSR(act.id)
          }

          return {
            hash: digest,
            actRegistryDigest,
            currentSignatureBase64: act.base64signature,
          }
        } else {
          const secondaryDigestResponse = await getDigestAndPrepareSecondaryAct(
            {
              base64signature: signedHash,
              documentId: act.id,
              employmentId: user.employment.employmentId,
              imprint: imprint,
              keyIssuer: keyIssuer,
              signatureState: SignatureState.PENDING,
              certificateDateStart,
              certificateDateEnd,
              name: signerName,
              faceId: {
                faceImage: facePhoto,
                keySerialNumber,
              },
              withPoa,
              poaId,
              poaDateStart,
              poaDateEnd,
            },
          )

          return {
            hash: secondaryDigestResponse.digest,
            actRegistryDigest: secondaryDigestResponse.actRegistryDigest,
            currentSignatureBase64: act.base64signature,
          }
        }
      },
      signHandler: ({
        signedHash,
        actRegistryBase64signature,
        keyIssuer,
        imprint,
        facePhoto,
        keySerialNumber,
        signerName,
        withPoa,
        poaId,
        poaDateStart,
        poaDateEnd,
      }: SignHandlerParams) =>
        (isAosr ? signAct : signSecondaryAct)({
          documentId: act.id,
          employmentId: user.employment.employmentId,
          signatureState: SignatureState.SIGNED,
          base64signature: signedHash,
          actRegistryBase64signature,
          keyIssuer,
          imprint,
          name: signerName,
          faceId: {
            faceImage: facePhoto,
            keySerialNumber,
          },
          withPoa,
          poaId,
          poaDateStart,
          poaDateEnd,
        }).then(() => {
          refreshAct()
        }),
      onSuccess: () => {
        toast.success('Подписано')
        track('sign_act_success')
      },
      onFailure: () => {
        toast.error('Не удалось подписать')
        track('sign_act_fail')
        setIsLoading(false)
      },
    })
  }
}

export const getAssignees: (
  assigneesTemp,
  workType: WorkType,
) => { inspectors: UUID[]; signers: UUID[] } = (assigneesTemp, workType) => {
  const assignees =
    assigneesTemp.state === 'hasValue' ? assigneesTemp.contents : []

  const selectedInspectors: UUID[] = _.union(
    assignees
      .filter(
        assignee =>
          assignee.canInspect && assignee.workTypes.includes(workType),
      )
      .map(assignee => assignee.user.employment.employmentId),
  )

  const selectedSigners: UUID[] = _.union(
    assignees
      .filter(
        assignee => assignee.canSign && assignee.workTypes.includes(workType),
      )
      .map(assignee => assignee.user.employment.employmentId),
  )

  return { inspectors: selectedInspectors, signers: selectedSigners }
}
